import React, { createContext, useContext, useState } from 'react'

import {
	CheckCircle2Icon,
	AlertCircleIcon,
	AlertTriangleIcon,
	InfoIcon,
} from 'lucide-react'

import {
	Toast,
	ToastClose,
	ToastDescription,
	ToastProvider,
	ToastTitle,
	ToastViewport,
} from '@/shared/components/atoms/Toast'

export type NotificationType = 'success' | 'info' | 'warning' | 'error'

export interface NotificationProps {
	type: NotificationType
	message: string
	description?: React.ReactNode
	duration?: number
	btn?: React.ReactNode
	onClose?: () => void
}

interface ToastItem extends NotificationProps {
	id: string
	open: boolean
}

interface NotificationContextValue {
	showNotification: (props: NotificationProps) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [toasts, setToasts] = useState<ToastItem[]>([])

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id))
	}

	const showNotification = (props: NotificationProps) => {
		const id = Math.random().toString(36).substr(2, 9)
		const newToast: ToastItem = {
			...props,
			id,
			open: true,
		}

		setToasts((prev) => [...prev, newToast])

		const durationMs = (props.duration ?? 5) * 1000
		window.setTimeout(() => {
			removeToast(id)
		}, durationMs)
	}

	const getIcon = (type: NotificationType) => {
		switch (type) {
			case 'success':
				return <CheckCircle2Icon className="h-5 w-5" />
			case 'error':
				return <AlertCircleIcon className="h-5 w-5" />
			case 'warning':
				return <AlertTriangleIcon className="h-5 w-5" />
			case 'info':
				return <InfoIcon className="h-5 w-5" />
			default:
				return <InfoIcon className="h-5 w-5" />
		}
	}

	return (
		<NotificationContext.Provider value={{ showNotification }}>
			<ToastProvider duration={5000}>
				{children}
				{toasts.map((toast) => (
					<Toast
						key={toast.id}
						variant={toast.type}
						open={toast.open}
						onOpenChange={(open) => {
							if (!open) {
								removeToast(toast.id)
								toast.onClose?.()
							}
						}}
						duration={toast.duration ? toast.duration * 1000 : 5000}
					>
						<div className="flex gap-3">
							<div className="flex-shrink-0 pt-0.5">{getIcon(toast.type)}</div>
							<div className="flex-1">
								<ToastTitle>{toast.message}</ToastTitle>
								{toast.description && (
									<ToastDescription>{toast.description}</ToastDescription>
								)}
								{toast.btn && <div className="mt-2">{toast.btn}</div>}
							</div>
						</div>
						<ToastClose />
					</Toast>
				))}
				<ToastViewport />
			</ToastProvider>
		</NotificationContext.Provider>
	)
}

export const useNotificationContext = () => {
	const context = useContext(NotificationContext)
	if (!context) {
		throw new Error(
			'useNotificationContext must be used within a NotificationProvider'
		)
	}
	return context
}
